import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';

/**
 * A mecânica de UM trilho horizontal: mede se ainda há conteúdo para cada lado
 * e rola ~85% da janela visível por clique.
 *
 * Nasceu no dock de ofertas de hotel e subiu duas vezes de casa, pela mesma
 * razão das duas: uma segunda cópia da medição divergiria no primeiro ajuste de
 * calha. Subiu para `shared/` (e perdeu o `Hotel` do nome) quando o trilho de
 * famílias de tarifa do aéreo passou a usá-la — um nome de domínio numa peça
 * que serve dois domínios mente sobre onde ela pode aparecer.
 *
 * `measure` é chamado pelo `onScroll` do próprio trilho, por um
 * `ResizeObserver` e por um `MutationObserver` dos itens: a seta some no fim da
 * rolagem, reaparece quando a coluna encolhe e acompanha os itens que entram e
 * saem, sem nenhum estado espelhado do lado de fora.
 */
export function useRail<T extends HTMLElement = HTMLUListElement>() {
  // Genérico com default no `<ul>`: o dock de hotel é uma lista de verdade, e o
  // trilho de famílias de tarifa é um `radiogroup` (`<div>`) — `radiogroup` não
  // admite `listitem` dentro, então lá não há `<ul>` a referenciar.
  const listRef = useRef<T>(null);
  const [available, setAvailable] = useState({ previous: false, next: false });
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const next = {
      previous: list.scrollLeft > 8,
      next: list.scrollLeft + list.clientWidth < list.scrollWidth - 8,
    };
    setAvailable((current) =>
      current.previous === next.previous && current.next === next.next ? current : next,
    );
  }, []);
  useEffect(() => {
    measure();
    const list = listRef.current;
    let observer: ResizeObserver | undefined;
    if (list && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(list);
    }
    // Items entering or leaving change what is left to scroll, not the list's box.
    let items: MutationObserver | undefined;
    if (list && typeof MutationObserver !== 'undefined') {
      items = new MutationObserver(measure);
      items.observe(list, { childList: true });
    }
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      items?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);
  const scroll = (direction: -1 | 1) => {
    const list = listRef.current;
    if (!list) return;
    const limit = list.scrollWidth - list.clientWidth;
    const left = Math.max(
      0,
      Math.min(limit, list.scrollLeft + direction * list.clientWidth * 0.85),
    );
    list.scrollTo({ left, behavior: 'smooth' });
  };
  return { listRef, available, measure, scroll };
}

/** A seta de um trilho. Posicionada pelo CSS do trilho que a hospeda — o
 *  `data-direction` é o gancho, e cada folha decide a calha. */
export function RailArrow({
  direction,
  label,
  onClick,
}: {
  readonly direction: 'previous' | 'next';
  readonly label: string;
  readonly onClick: () => void;
}): ReactNode {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="hui-rail-arrow shadow-md"
      data-direction={direction}
      aria-label={label}
      onClick={onClick}
    >
      {direction === 'previous' ? (
        <CaretLeftIcon size={16} aria-hidden />
      ) : (
        <CaretRightIcon size={16} aria-hidden />
      )}
    </Button>
  );
}
